import { Slot } from "@radix-ui/react-slot";
import type { AnchorHTMLAttributes, HTMLAttributes, TdHTMLAttributes, ThHTMLAttributes } from "react";
import { cn } from "../lib/cn";

/**
 * Meridian's table (docs/design/meridian.md): the header on `subtle` in secondary text, 40 px rows, numbers right
 * aligned and tabular, and a row that opens its record through the `RowLink` in one of its cells.
 */
export function Table({ className, ...props }: HTMLAttributes<HTMLTableElement>) {
  return (
    <div className="overflow-x-auto">
      <table className={cn("w-full border-collapse text-small", className)} {...props} />
    </div>
  );
}

export function TableHead(props: HTMLAttributes<HTMLTableSectionElement>) {
  return <thead {...props} />;
}

export function TableBody(props: HTMLAttributes<HTMLTableSectionElement>) {
  return <tbody {...props} />;
}

export function TableRow({ className, ...props }: HTMLAttributes<HTMLTableRowElement>) {
  return (
    <tr
      className={cn(
        "relative border-b border-border-subtle transition-colors duration-(--duration-fast) last:border-b-0 [tbody_&]:hover:bg-subtle",
        className,
      )}
      {...props}
    />
  );
}

const edge = "px-3 first:ps-6 last:pe-6";

export function TableHeaderCell({
  numeric = false,
  className,
  ...props
}: ThHTMLAttributes<HTMLTableCellElement> & { numeric?: boolean }) {
  return (
    <th
      scope="col"
      className={cn(
        edge,
        "border-b border-border bg-subtle py-2.5 text-start font-medium text-ink-muted",
        numeric && "text-end",
        className,
      )}
      {...props}
    />
  );
}

export function TableCell({
  numeric = false,
  className,
  ...props
}: TdHTMLAttributes<HTMLTableCellElement> & { numeric?: boolean }) {
  return (
    <td
      className={cn(
        edge,
        "h-10 py-2.5 text-ink-body",
        numeric && "text-end font-medium text-ink tabular-nums",
        className,
      )}
      {...props}
    />
  );
}

/**
 * The link that opens a row's record. Its click area covers the whole row, while it stays one link for keyboards
 * and screen readers. `asChild` takes the app's router link.
 */
export function RowLink({
  asChild = false,
  className,
  ...props
}: AnchorHTMLAttributes<HTMLAnchorElement> & { asChild?: boolean }) {
  const Component = asChild ? Slot : "a";
  return (
    <Component
      className={cn(
        "font-medium text-ink after:absolute after:inset-0 after:content-[''] hover:text-brand-700",
        className,
      )}
      {...props}
    />
  );
}
